import { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tagService, studentService, artworkService } from '../../services';

// Types
interface EditArtworkModalProps {
  isOpen: boolean;
  artworkId: number | null;
  artworkIds?: number[]; // For bulk operations
  currentTags?: string[];
  studentId?: number | null;
  onClose: () => void;
  onSuccess?: () => void;
}

// Styled Components
const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3000;
  opacity: ${({ isOpen }) => isOpen ? 1 : 0};
  visibility: ${({ isOpen }) => isOpen ? 'visible' : 'hidden'};
  transition: all ${({ theme }) => theme.transitions.normal};
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  max-width: 600px;
  width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${({ theme }) => theme.shadows.xl};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  padding-bottom: ${({ theme }) => theme.spacing.md};
  border-bottom: 2px solid ${({ theme }) => theme.colors.gray.light};
`;

const ModalTitle = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.gray.dark};
  padding: ${({ theme }) => theme.spacing.xs};
  
  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`;

const FormSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const StudentSelect = styled.select`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm};
  border: 2px solid ${({ theme }) => theme.colors.gray.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;



const CurrentTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
  min-height: 40px;
  padding: ${({ theme }) => theme.spacing.sm};
  border: 2px solid ${({ theme }) => theme.colors.gray.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.background};
`;

const TagChip = styled.span<{ removable?: boolean }>`
  background: ${({ theme }) => theme.colors.primary}20;
  color: ${({ theme }) => theme.colors.primary};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  cursor: ${({ removable }) => removable ? 'pointer' : 'default'};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background: ${({ removable, theme }) => removable ? `${theme.colors.primary}30` : `${theme.colors.primary}20`};
  }
`;

const RemoveButton = styled.span`
  font-size: 14px;
  font-weight: bold;
  opacity: 0.7;
  
  &:hover {
    opacity: 1;
  }
`;

const EmptyTagsMessage = styled.div`
  color: ${({ theme }) => theme.colors.gray.dark};
  font-style: italic;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
`;

const AvailableTagsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: ${({ theme }) => theme.spacing.xs};
  max-height: 200px;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.sm};
  border: 2px solid ${({ theme }) => theme.colors.gray.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const AvailableTag = styled.button<{ selected: boolean }>`
  background: ${({ selected, theme }) => selected ? theme.colors.primary : theme.colors.gray.light};
  color: ${({ selected, theme }) => selected ? theme.colors.white : theme.colors.text};
  border: none;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  text-align: center;
  
  &:hover {
    background: ${({ selected, theme }) => selected ? `${theme.colors.primary}dd` : theme.colors.gray.medium};
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm};
  border: 2px solid ${({ theme }) => theme.colors.gray.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing.xl};
  padding-top: ${({ theme }) => theme.spacing.md};
  border-top: 2px solid ${({ theme }) => theme.colors.gray.light};
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  background: ${({ variant, theme }) => 
    variant === 'primary' ? theme.colors.primary : theme.colors.gray.medium};
  color: ${({ variant, theme }) => 
    variant === 'primary' ? theme.colors.white : theme.colors.text};
  border: none;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover:not(:disabled) {
    background: ${({ variant, theme }) => 
      variant === 'primary' ? `${theme.colors.primary}dd` : theme.colors.gray.dark};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.gray.dark};
`;

const ErrorMessage = styled.div`
  background: ${({ theme }) => theme.colors.error}10;
  border: 2px solid ${({ theme }) => theme.colors.error};
  color: ${({ theme }) => theme.colors.error};
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

export function EditArtworkModal({
  isOpen,
  artworkId,
  artworkIds,
  currentTags = [],
  studentId,
  onClose,
  onSuccess
}: EditArtworkModalProps) {
  const queryClient = useQueryClient();
  const [selectedTagIds, setSelectedTagIds] = useState<Set<number>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(studentId || null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Determine if this is a bulk operation
  const isBulkOperation = Boolean(artworkIds && artworkIds.length > 1);
  const targetArtworkIds = artworkIds || (artworkId ? [artworkId] : []);
  const bulkCount = targetArtworkIds.length;

  // Data fetching
  const { data: tagsData, isLoading: loadingTags } = useQuery({
    queryKey: ['admin', 'tags'],
    queryFn: () => tagService.getAdminTags({ includeUsageCount: true, limit: 100 }),
    enabled: isOpen,
  });

  const { data: students = [] } = useQuery({
    queryKey: ['admin', 'students-list'],
    queryFn: () => studentService.getStudents(1, 100),
    select: (data) => data.students,
    enabled: isOpen,
  });

  const tags = useMemo(() => tagsData?.tags || [], [tagsData]);

  // Initialize selected tags and student when modal opens
  useEffect(() => {
    if (isOpen) {
      // Clear error message
      setErrorMessage(null);

      // Initialize tags
      if (currentTags.length > 0) {
        const currentTagIds = new Set<number>();
        currentTags.forEach(tagName => {
          const tag = tags.find(t => t.tag_name === tagName);
          if (tag) {
            currentTagIds.add(tag.tag_id);
          }
        });
        setSelectedTagIds(currentTagIds);
      } else {
        setSelectedTagIds(new Set());
      }

      // Initialize student
      setSelectedStudentId(studentId || null);
    }
  }, [isOpen, currentTags, tags, studentId]);

  // Reset search when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  // Update tags mutation - handles both single and bulk operations
  const updateTagsMutation = useMutation({
    mutationFn: async (tagIds: number[]) => {
      if (targetArtworkIds.length === 0) throw new Error('No artwork IDs provided');
      
      // For bulk operations, apply tags to all selected artworks
      const results = await Promise.all(
        targetArtworkIds.map(id => tagService.setArtworkTags(id, tagIds))
      );
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'artworks'] });
    },
  });

  // Update student mutation - handles both single and bulk operations
  const updateStudentMutation = useMutation({
    mutationFn: async (studentId: number | null) => {
      if (targetArtworkIds.length === 0) throw new Error('No artwork IDs provided');
      
      // For bulk operations, update student for all selected artworks
      const results = await Promise.all(
        targetArtworkIds.map(id => artworkService.updateArtworkStudent(id, studentId))
      );
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'artworks'] });
    },
  });

  // Event handlers
  const handleTagToggle = (tagId: number) => {
    const newSelected = new Set(selectedTagIds);
    if (newSelected.has(tagId)) {
      newSelected.delete(tagId);
    } else {
      newSelected.add(tagId);
    }
    setSelectedTagIds(newSelected);
  };

  const handleRemoveTag = (tagId: number) => {
    const newSelected = new Set(selectedTagIds);
    newSelected.delete(tagId);
    setSelectedTagIds(newSelected);
  };

  const handleSave = async () => {
    try {
      setErrorMessage(null);

      // Update tags for all selected artworks
      await updateTagsMutation.mutateAsync(Array.from(selectedTagIds));

      // Only update student for single artwork operations
      if (!isBulkOperation && selectedStudentId !== studentId) {
        await updateStudentMutation.mutateAsync(selectedStudentId);
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to update artwork(s):', error);
      const errorMsg = error instanceof Error ? error.message : 'Failed to update artwork. Please try again.';
      setErrorMessage(errorMsg);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  // Filter available tags
  const filteredTags = tags.filter(tag =>
    tag.tag_name.toLowerCase().includes(searchTerm.toLowerCase())
  );


  // Get selected tag objects
  const selectedTags = tags.filter(tag => selectedTagIds.has(tag.tag_id));

  if (!isOpen) return null;

  return (
    <ModalOverlay isOpen={isOpen} onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            {isBulkOperation ? `Edit ${bulkCount} Artworks` : 'Edit Artwork Details'}
          </ModalTitle>
          <CloseButton onClick={onClose}>✕</CloseButton>
        </ModalHeader>

        {/* Error Message */}
        {errorMessage && (
          <ErrorMessage>
            {errorMessage}
          </ErrorMessage>
        )}

        {/* Hide student selection for bulk operations */}
        {!isBulkOperation && (
          <FormSection>
            <SectionTitle>Student</SectionTitle>
            <StudentSelect
              value={selectedStudentId || ''}
              onChange={(e) => setSelectedStudentId(e.target.value ? parseInt(e.target.value) : null)}
            >
              <option value="">No Student Assigned</option>
              {students.map(student => (
                <option key={student.student_id} value={student.student_id}>
                  {student.name}
                </option>
              ))}
            </StudentSelect>
          </FormSection>
        )}

        <FormSection>
          <SectionTitle>Current Tags ({selectedTags.length})</SectionTitle>
          <CurrentTags>
            {selectedTags.length > 0 ? (
              selectedTags.map(tag => (
                <TagChip key={tag.tag_id} removable>
                  {tag.tag_name}
                  <RemoveButton onClick={() => handleRemoveTag(tag.tag_id)}>
                    ✕
                  </RemoveButton>
                </TagChip>
              ))
            ) : (
              <EmptyTagsMessage>No tags selected</EmptyTagsMessage>
            )}
          </CurrentTags>
        </FormSection>

        <FormSection>
          <SectionTitle>Available Tags</SectionTitle>
          <SearchInput
            type="text"
            placeholder="Search tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          
          {loadingTags ? (
            <LoadingSpinner>Loading tags...</LoadingSpinner>
          ) : (
            <AvailableTagsGrid>
              {filteredTags.map(tag => (
                <AvailableTag
                  key={tag.tag_id}
                  selected={selectedTagIds.has(tag.tag_id)}
                  onClick={() => handleTagToggle(tag.tag_id)}
                >
                  {tag.tag_name}
                  {tag.usage_count !== undefined && (
                    <div style={{ fontSize: '10px', opacity: 0.7 }}>
                      ({tag.usage_count})
                    </div>
                  )}
                </AvailableTag>
              ))}
            </AvailableTagsGrid>
          )}
        </FormSection>

        <ModalActions>
          <ActionButton onClick={handleCancel} disabled={updateTagsMutation.isPending}>
            Cancel
          </ActionButton>
          <ActionButton 
            variant="primary" 
            onClick={handleSave}
            disabled={updateTagsMutation.isPending || updateStudentMutation.isPending}
          >
            {(updateTagsMutation.isPending || updateStudentMutation.isPending) ? 'Saving...' : 'Save Changes'}
          </ActionButton>
        </ModalActions>
      </ModalContent>
    </ModalOverlay>
  );
}

export default EditArtworkModal;
